import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Loader2, FileText, Image as ImageIcon, Trash2, Clock3 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge, PriorityBadge } from "@/components/shared/StatusBadge";
import { FileUploader } from "@/components/shared/FileUploader";
import { useToast } from "@/components/ui/toast";
import {
  useComplaint,
  useChangeStatus,
  useAssignEmployee,
  useAddNote,
  useLogHours,
  useUploadAttachment,
  useRemoveAttachment,
} from "@/hooks/useComplaints";
import { useActiveEmployees } from "@/hooks/useEmployees";
import { useAuthStore } from "@/store/auth.store";
import { nextStatusOptions } from "./statusFlow";
import { ComplaintTimeline } from "./ComplaintTimeline";
import { noteFormSchema, NoteFormValues, hoursFormSchema, HoursFormValues } from "./complaint.schemas";
import { isAxiosError } from "axios";
import { ComplaintStatus } from "@/components/shared/StatusBadge";

interface ComplaintDetailDialogProps {
  complaintId: string | null;
  onOpenChange: (open: boolean) => void;
}

function errorMessage(err: unknown, fallback: string): string {
  return isAxiosError(err)
    ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message ?? fallback
    : fallback;
}

export function ComplaintDetailDialog({ complaintId, onOpenChange }: ComplaintDetailDialogProps) {
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.role);
  const { data: complaint, isLoading } = useComplaint(complaintId ?? undefined);
  const { data: employees } = useActiveEmployees();

  const changeStatusMutation = useChangeStatus(complaintId ?? "");
  const assignMutation = useAssignEmployee(complaintId ?? "");
  const addNoteMutation = useAddNote(complaintId ?? "");
  const logHoursMutation = useLogHours(complaintId ?? "");
  const uploadMutation = useUploadAttachment(complaintId ?? "");
  const removeAttachmentMutation = useRemoveAttachment(complaintId ?? "");

  const [pendingStatus, setPendingStatus] = useState("");

  const noteForm = useForm<NoteFormValues>({ resolver: zodResolver(noteFormSchema) });
  const hoursForm = useForm<HoursFormValues>({ resolver: zodResolver(hoursFormSchema) });

  if (!complaintId) return null;

  const handleStatusChange = async () => {
    if (!pendingStatus) return;
    try {
      await changeStatusMutation.mutateAsync({ status: pendingStatus as ComplaintStatus });
      toast({ title: `Status updated to ${pendingStatus}`, variant: "success" });
      setPendingStatus("");
    } catch (err) {
      toast({ title: "Could not update status", description: errorMessage(err, ""), variant: "destructive" });
    }
  };

  const handleAssign = async (employeeId: string) => {
    if (!employeeId) return;
    try {
      await assignMutation.mutateAsync({ employeeId });
      toast({ title: "Job assigned", variant: "success" });
    } catch (err) {
      toast({ title: "Could not assign job", description: errorMessage(err, ""), variant: "destructive" });
    }
  };

  const onAddNote = async (values: NoteFormValues) => {
    try {
      await addNoteMutation.mutateAsync(values.remarks);
      noteForm.reset();
      toast({ title: "Note added", variant: "success" });
    } catch (err) {
      toast({ title: "Could not add note", description: errorMessage(err, ""), variant: "destructive" });
    }
  };

  const onLogHours = async (values: HoursFormValues) => {
    try {
      await logHoursMutation.mutateAsync({ workingHours: values.workingHours, remarks: values.remarks });
      hoursForm.reset();
      toast({ title: "Hours logged", variant: "success" });
    } catch (err) {
      toast({ title: "Could not log hours", description: errorMessage(err, ""), variant: "destructive" });
    }
  };

  const handleUpload = async (file: File) => {
    try {
      await uploadMutation.mutateAsync(file);
      toast({ title: "File uploaded", variant: "success" });
    } catch (err) {
      toast({ title: "Upload failed", description: errorMessage(err, ""), variant: "destructive" });
    }
  };

  return (
    <Dialog open={Boolean(complaintId)} onOpenChange={(open) => !open && onOpenChange(false)}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        {isLoading || !complaint ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle className="font-data">{complaint.complaintNumber}</DialogTitle>
                <StatusBadge status={complaint.status} />
                <PriorityBadge priority={complaint.priority} />
              </div>
              <DialogDescription>
                {complaint.customer.fullName} · {complaint.customer.phone} · {complaint.productName}
                {complaint.brand ? ` (${complaint.brand}${complaint.model ? ` ${complaint.model}` : ""})` : ""}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="overview">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="attachments">Attachments ({complaint.attachments.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-5">
                <p className="text-sm text-muted-foreground">{complaint.description}</p>

                <div className="grid grid-cols-2 gap-4 rounded-md border border-border bg-muted/30 p-4 text-sm">
                  <div className="space-y-1.5">
                    <Label>Update status</Label>
                    <div className="flex gap-2">
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm"
                        value={pendingStatus}
                        onChange={(e) => setPendingStatus(e.target.value)}
                      >
                        <option value="">Select next status</option>
                        {nextStatusOptions(complaint.status).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        onClick={handleStatusChange}
                        disabled={!pendingStatus || changeStatusMutation.isPending}
                      >
                        {changeStatusMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Update
                      </Button>
                    </div>
                  </div>

                  {role === "ADMIN" && (
                    <div className="space-y-1.5">
                      <Label>Assign technician</Label>
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm"
                        value={complaint.assignedEmployee?.id ?? ""}
                        onChange={(e) => handleAssign(e.target.value)}
                        disabled={assignMutation.isPending}
                      >
                        <option value="">Unassigned</option>
                        {employees?.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.fullName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {complaint.expectedCompletionDate && (
                    <div>
                      <Label className="text-muted-foreground">Expected completion</Label>
                      <p>{format(new Date(complaint.expectedCompletionDate), "dd MMM yyyy")}</p>
                    </div>
                  )}
                  {complaint.serialNumber && (
                    <div>
                      <Label className="text-muted-foreground">Serial number</Label>
                      <p className="font-data">{complaint.serialNumber}</p>
                    </div>
                  )}
                </div>

                <form onSubmit={noteForm.handleSubmit(onAddNote)} className="space-y-1.5">
                  <Label htmlFor="note">Add a note</Label>
                  <div className="flex gap-2">
                    <Input id="note" placeholder="What did you find or do?" {...noteForm.register("remarks")} />
                    <Button type="submit" size="sm" disabled={addNoteMutation.isPending}>
                      {addNoteMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      Add
                    </Button>
                  </div>
                  {noteForm.formState.errors.remarks && (
                    <p className="text-xs text-destructive">{noteForm.formState.errors.remarks.message}</p>
                  )}
                </form>

                <form onSubmit={hoursForm.handleSubmit(onLogHours)} className="space-y-1.5">
                  <Label htmlFor="hours" className="flex items-center gap-1.5">
                    <Clock3 className="h-3.5 w-3.5" /> Log working hours
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="hours"
                      type="number"
                      step="0.5"
                      placeholder="e.g. 1.5"
                      className="w-28"
                      {...hoursForm.register("workingHours")}
                    />
                    <Input placeholder="Remarks (optional)" {...hoursForm.register("remarks")} />
                    <Button type="submit" size="sm" disabled={logHoursMutation.isPending}>
                      {logHoursMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      Log
                    </Button>
                  </div>
                  {hoursForm.formState.errors.workingHours && (
                    <p className="text-xs text-destructive">{hoursForm.formState.errors.workingHours.message}</p>
                  )}
                </form>
              </TabsContent>

              <TabsContent value="timeline">
                <ComplaintTimeline events={complaint.histories} />
              </TabsContent>

              <TabsContent value="attachments" className="space-y-4">
                <FileUploader onUpload={handleUpload} isUploading={uploadMutation.isPending} />
                {complaint.attachments.length === 0 ? (
                  <p className="py-4 text-center text-sm text-muted-foreground">No files uploaded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {complaint.attachments.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                      >
                        <div className="flex items-center gap-2">
                          {a.fileType === "PHOTO" ? (
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <FileText className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="truncate">{a.fileName}</span>
                          <span className="text-xs text-muted-foreground">
                            {(a.fileSize / 1024).toFixed(0)} KB
                          </span>
                        </div>
                        {role === "ADMIN" && (
                          <button
                            onClick={() => removeAttachmentMutation.mutate(a.id)}
                            className="text-muted-foreground hover:text-destructive"
                            aria-label="Delete attachment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
